import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import 'moment/dist/locale/es';
import LocizeBackend from 'i18next-locize-backend';
import HttpBackend from 'i18next-http-backend';
import { localStorageGetItem, localStorageAvailable } from '@/utils/storage/storage-available';
import { defaultLang } from './config-lang';

const locizeOptions = {
  projectId: 'c076f7d5-0c54-4d8b-bfb3-9ba1b878f748',
  apiKey: '11c4b0c2-4eb6-41a7-86ce-172687d10890',
  referenceLng: 'en',
  version: 'latest',
  // Add the required Locize URLs
  loadPath: 'https://api.locize.app/{{projectId}}/{{version}}/{{lng}}/{{ns}}',
  addPath: 'https://api.locize.app/missing/{{projectId}}/{{version}}/{{lng}}/{{ns}}',
  updatePath: 'https://api.locize.app/update/{{projectId}}/{{version}}/{{lng}}/{{ns}}',
};

const callback = (error?: Error, t?: any) => {
  if (error) {
    Sentry.captureException(error);
    return;
  }
};

// ----------------------------------------------------------------------

// Get stored language or detect from browser
const getInitialLanguage = () => {
  const storedLng = localStorageGetItem('i18nextLng');
  if (storedLng) return storedLng;

  // Detect browser language
  const browserLng = navigator.language || navigator.languages[0];
  const shortLng = browserLng.split('-')[0]; // Get 'es' from 'es-ES'

  // Check if the detected language is supported
  const supportedLngs = ['en', 'es'];
  return supportedLngs.includes(shortLng) ? shortLng : defaultLang.value;
};

const lng = getInitialLanguage();

const LOCIZE_TRANSLATE = false;

// Cache configuration for CDN translations
const CACHE_KEY_PREFIX = 'i18n_cache_';
const CACHE_VERSION = '1.0.0';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const getCachedTranslations = (lng: string) => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${lng}_${CACHE_VERSION}`;
    const cached = localStorageGetItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      // Check if cache is still valid
      if (now - timestamp < CACHE_TTL) {
        return data;
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return null;
};

const setCachedTranslations = (lng: string, data: any) => {
  try {
    if (localStorageAvailable()) {
      const cacheKey = `${CACHE_KEY_PREFIX}${lng}_${CACHE_VERSION}`;
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    }
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

// Custom backend with cache support
const cachedHttpBackend = {
  type: 'backend' as const,
  init: function (this: any, services: any, backendOptions: any, i18nextOptions: any) {
    this.services = services;
    this.options = backendOptions;
  },
  read: function (language: string, namespace: string, callback: Function) {
    const cachedData = getCachedTranslations(language);

    if (cachedData) {
      // Return cached data immediately
      callback(null, cachedData);
      return;
    }

    // Fetch from CDN
    const url = `https://cdn.orion.global/hub/statics/locales/${language}/translation.json`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // Cache the translations
        setCachedTranslations(language, data);
        callback(null, data);
      })
      .catch((error) => {
        callback(error, null);
      });
  },
};

i18n
  .use<any>(LOCIZE_TRANSLATE ? LocizeBackend : cachedHttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init<any>({
    lng,
    fallbackLng: ['en', 'es'],
    saveMissing: LOCIZE_TRANSLATE,
    saveMissingTo: 'all',
    debug: LOCIZE_TRANSLATE,
    interpolation: {
      escapeValue: false,
    },
    backend: LOCIZE_TRANSLATE ? locizeOptions : {},
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      excludeCacheFor: ['cimode'],
    },
  });

i18n.on('languageChanged', (newLng) => {
  moment.locale(newLng);
  // Save the selected language
  if (localStorageAvailable()) {
    localStorage.setItem('i18nextLng', newLng);
  }
});

export default i18n;
